import { defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";
import { mapState } from "pinia";

import { useConvertToPendingPal } from "./../../store/useConvertToPendingPal";
import { TeamsService } from "../../../../../services/teams.service";
import { UserService } from "../../../../../services/user-service";
import { TolerablePainLevels } from "../../../../../enums/enums";
import { ITypeaheadValue } from "../../../../../resources-vue/vue-interfaces/i-typeahead";
import {
    IDiagnosis,
    IDiagnosisChangeParameter,
    IDiagnosisValue
} from "../../../../../resources-vue/vue-interfaces/i-diagnosis";
import { IActiveUser, IHosUserResponse } from "../../../../../resources-vue/vue-interfaces/i-user";
import { IGetTeamLean } from "../../../../../resources-vue/vue-interfaces/i-team";

import Diagnosis from "../../../../../resources-vue/vue-custom-elements/Diagnosis/Diagnosis.vue";
import FormSection from "../../../../../resources-vue/vue-custom-elements/FormSection/FormSection.vue";
import TypeaheadInput from "../../../../../resources-vue/vue-custom-elements/TypeaheadInput/TypeaheadInput.vue";
import DiagnosisTable from "./DiagnosisTable/DiagnosisTable.vue";
import ProblemsTable from "./ProblemsTable/ProblemsTable.vue";

export default defineComponent({
    components: {
        FormSection,
        TypeaheadInput,
        Diagnosis,
        DiagnosisTable,
        ProblemsTable
    },

    data(vm) {
        return {
            _userService: null,
            _teamsService: null,
            caseManager: null as ITypeaheadValue,
            clinicalManager: null as ITypeaheadValue,
            admittingRn: null as ITypeaheadValue,
            teams: [] as IGetTeamLean[],
            admittingRN: null as ITypeaheadValue,
            spiritualCounselor: null as ITypeaheadValue,
            socialWorker: null as ITypeaheadValue,
            referralDiagnosis: null,
            admittingDiagnosis: null as IDiagnosis,
            aide: null as ITypeaheadValue,
            volunteerCoordinator: null as ITypeaheadValue,
            bereavementCoordinator: null as ITypeaheadValue,
            TolerablePainLevels: Array.from(TolerablePainLevels.enumWithDescriptions.values())
        };
    },

    created() {
        this._teamsService = Container.instance.get(TeamsService);
        this._userService = Container.instance.get(UserService);
        if (this.patientDetails.clinicalInformation.referringDiagnosis) {
            this.referralDiagnosis = this.patientDetails.clinicalInformation.referringDiagnosis;
        }
    },

    mounted() {
        this.getTeams();

        if (this.patientDetails.clinicalInformation.caseManagerId) {
            const caseManagerResponse = this.getUsers(this.patientDetails.clinicalInformation.caseManagerId);
            caseManagerResponse.then((users) => {
                this.caseManager = users;
            });
        }
        if (this.patientDetails.clinicalInformation.clinicalManagerId) {
            const clinicalManagerResponse = this.getUsers(this.patientDetails.clinicalInformation.clinicalManagerId);
            clinicalManagerResponse.then((users) => {
                this.clinicalManager = users;
            });
        }
        if (this.patientDetails.clinicalInformation.admittingNurseId) {
            const admittingNurseIdResponse = this.getUsers(this.patientDetails.clinicalInformation.admittingNurseId);
            admittingNurseIdResponse.then((users) => {
                this.admittingRn = users;
            });
        }
        if (this.patientDetails.clinicalInformation.spiritualCounselorId) {
            const spiritualCounselorIdResponse = this.getUsers(
                this.patientDetails.clinicalInformation.spiritualCounselorId
            );
            spiritualCounselorIdResponse.then((users) => {
                this.spiritualCounselor = users;
            });
        }
        if (this.patientDetails.clinicalInformation.socialWorkerId) {
            const socialWorkerIdIdResponse = this.getUsers(this.patientDetails.clinicalInformation.socialWorkerId);
            socialWorkerIdIdResponse.then((users) => {
                this.socialWorker = users;
            });
        }
        if (this.patientDetails.clinicalInformation.aideId) {
            const aideIdIdResponse = this.getUsers(this.patientDetails.clinicalInformation.aideId);
            aideIdIdResponse.then((users) => {
                this.aide = users;
            });
        }
    },

    computed: {
        ...mapState(useConvertToPendingPal, [
            "application",
            "patientDetails",
            "accountId",
            "patientId",
            "patientOptions"
        ])
    },

    methods: {
        async getUsers(id: string) {
            const response = await this._userService.getUserById({
                id: id,
                accountId: this.accountId,
                application: this.application
            });

            return {
                name: `${response.FirstName} ${response.LastName}`,
                value: response.Id
            };
        },
        async getTeams() {
            try {
                if (!!this.patientDetails) {
                    const payload = {
                        providerIds: [this.patientOptions.data.providerId],
                        locationIds: [this.patientOptions.data.branchId],
                        agencyId: this.accountId,
                        accountId: this.accountId,
                        application: this.application
                    };
                    this.teams = await this._teamsService.getTeamsLean(payload);
                }
            } catch (e) {
                console.error(e);
            }
        },
        async getUsersList(filter: string, limit: number) {
            const response = await this._userService.getUsersByRole({
                include: true,
                term: filter,
                application: this.application
            });
            if (response) {
                return response.map((user: IHosUserResponse) => ({
                    name: `${user.firstName} ${user.lastName}`,
                    value: user.id
                }));
            }
        },
        async getActiveList(filter: string, limit: number) {
            const response = await this._userService.getActiveList({
                AccountId: this.accountId,
                LocationIds: [this.patientOptions.data.branchId],
                Term: filter,
                Application: this.application
            });
            if (response) {
                return response.map((activeUser: IActiveUser) => ({
                    name: `${activeUser.firstName} ${activeUser.lastName}`,
                    value: activeUser.id
                }));
            }
        },
        updateCaseManager(newValue: ITypeaheadValue) {
            if (newValue) {
                this.caseManager = newValue;
                this.patientDetails.clinicalInformation.caseManagerId = newValue.value as string;
            }
        },
        updateClinicalManager(newValue: ITypeaheadValue) {
            if (newValue) {
                this.clinicalManager = newValue;
                this.patientDetails.clinicalInformation.clinicalManagerId = newValue.value as string;
            }
        },
        updateAdmittingRN(newValue: ITypeaheadValue) {
            if (newValue) {
                this.admittingRN = newValue;
                this.patientDetails.clinicalInformation.admittingNurseId = newValue.value as string;
            }
        },
        updateSpiritualCounselor(newValue: ITypeaheadValue) {
            if (newValue) {
                this.spiritualCounselor = newValue;
                this.patientDetails.clinicalInformation.spiritualCounselorId = newValue.value as string;
            }
        },
        updateSocialWorker(newValue: ITypeaheadValue) {
            if (newValue) {
                this.socialWorker = newValue;
                this.patientDetails.clinicalInformation.socialWorkerId = newValue.value as string;
            }
        },
        referralDiagnosisChanged(diag: IDiagnosisChangeParameter) {
            this.referralDiagnosis = diag?.newDiagnosis;
            this.patientDetails.clinicalInformation.referringDiagnosis = diag?.newDiagnosis;
        },
        updateDiagnosis(value: Array<IDiagnosisValue>) {
            this.patientDetails.clinicalInformation.diagnoses = value;
        },

        updateProblems(value: any) {
            this.patientDetails.clinicalInformation.problems = value;
        },
        updateAide(newValue: ITypeaheadValue) {
            if (newValue) {
                this.aide = newValue;
                this.patientDetails.clinicalInformation.aideId = newValue.value as string;
            }
        }
    }
});
